import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Activity, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';
import { format, subDays } from 'date-fns';

const COLORS = ['hsl(var(--primary))', 'hsl(142 76% 36%)', 'hsl(45 93% 47%)', 'hsl(217 91% 60%)', 'hsl(340 82% 52%)', 'hsl(280 65% 60%)', 'hsl(25 95% 53%)'];

interface ActivityRow {
  activity_type: string;
  activity_date: string;
  points_earned: number;
}

export const ActivityInsights: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const thirtyDaysAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      const { data } = await supabase
        .from('activities')
        .select('activity_type, activity_date, points_earned')
        .eq('user_id', user!.id)
        .gte('activity_date', thirtyDaysAgo)
        .order('activity_date', { ascending: true });

      setActivities(data ?? []);
    } catch (error) {
      console.error('Failed to fetch activity insights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('analytics.activity.noData', 'No Activities Yet')}</h3>
          <p className="text-sm text-muted-foreground">{t('analytics.activity.noDataDesc', 'Log more activities to see insights here.')}</p>
        </CardContent>
      </Card>
    );
  }

  // Group by type
  const byType: Record<string, { count: number; points: number }> = {};
  activities.forEach(a => {
    if (!byType[a.activity_type]) byType[a.activity_type] = { count: 0, points: 0 };
    byType[a.activity_type].count++;
    byType[a.activity_type].points += a.points_earned;
  });

  const typeData = Object.entries(byType)
    .map(([type, { count, points }]) => ({ activityType: type.replace(/_/g, ' '), count, totalPoints: points }))
    .sort((a, b) => b.count - a.count);

  const pieData = typeData.map(d => ({ name: d.activityType, value: d.count }));
  const totalActivities = activities.length;

  // Group by date for frequency chart
  const byDate: Record<string, number> = {};
  activities.forEach(a => {
    byDate[a.activity_date] = (byDate[a.activity_date] || 0) + 1;
  });
  const dateData = Object.entries(byDate)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6">
      {/* Activity Frequency */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.activity.frequency', 'Activity Frequency (30 days)')}</CardTitle>
          <CardDescription>{t('analytics.activity.logged', '{{count}} activities logged', { count: totalActivities })}</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dateData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={v => format(new Date(v), 'dd')} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
              <Tooltip
                labelFormatter={v => format(new Date(v as string), 'MMM dd')}
                contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
              />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={4} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Type Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.activity.distribution', 'Activity Distribution')}</CardTitle>
          <CardDescription>{t('analytics.activity.breakdownByType', 'Breakdown by type')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    dataKey="value"
                  >
                    {pieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground mb-3">{t('analytics.activity.summary', 'Activity Summary')}</h4>
              {typeData.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                    <div>
                      <div className="font-medium text-sm text-foreground capitalize">{item.activityType}</div>
                      <div className="text-xs text-muted-foreground">{item.totalPoints} {t('analytics.activity.ptsEarned', 'pts earned')}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm text-foreground">{item.count}</div>
                    <div className="text-xs text-muted-foreground">{((item.count / totalActivities) * 100).toFixed(0)}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
